import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { 
  User, 
  Settings, 
  Key, 
  LogOut, 
  ChevronDown,
  Mail,
  Building2,
  Shield,
  CreditCard,
  Bell,
  HelpCircle,
  Gift
} from "lucide-react";

export default function UserDropdown({ onNavigate }) {
  const navigate = useNavigate();
  const { user, company, subscription, logout, isSuperAdmin, hasAdminAccess } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigation = (tab) => {
    setIsOpen(false);
    if (onNavigate) {
      onNavigate(tab);
    }
  };

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate("/");
  };

  const menuItems = [
    {
      section: "Account",
      items: [
        { 
          icon: User, 
          label: "My Profile", 
          description: user?.email,
          onClick: () => handleNavigation("settings")
        },
        { 
          icon: Building2, 
          label: "Company Settings", 
          description: company?.name || company?.company_name || "Not set",
          onClick: () => handleNavigation("settings")
        },
      ]
    },
    {
      section: "Security",
      items: [
        { 
          icon: Key, 
          label: "Change Password", 
          onClick: () => handleNavigation("settings")
        },
        { 
          icon: Shield, 
          label: "Two-Factor Auth", 
          description: user?.two_factor_enabled ? "Enabled" : "Disabled",
          onClick: () => handleNavigation("settings")
        },
      ]
    },
    {
      section: "Subscription",
      items: [
        { 
          icon: CreditCard, 
          label: "Billing & Plans", 
          description: subscription?.plan_id?.replace("_", " ") || "Free",
          onClick: () => handleNavigation("billing")
        },
        { 
          icon: Gift, 
          label: "Referral Program", 
          description: "Earn £50 credit",
          onClick: () => handleNavigation("referrals")
        },
      ]
    },
    {
      section: "Help",
      items: [
        { 
          icon: HelpCircle, 
          label: "Support", 
          onClick: () => handleNavigation("support")
        },
      ]
    }
  ];

  // Add admin option if user has backoffice access
  if (hasAdminAccess) {
    menuItems.splice(1, 0, {
      section: "Admin",
      items: [
        { 
          icon: Shield, 
          label: "Admin Panel", 
          description: "Manage platform",
          onClick: () => {
            setIsOpen(false);
            // Navigate within the SAME tab to avoid spawning a new tab on every
            // click. Backoffice has its own auth gate so direct navigation
            // works fine; users can ⌘-click / middle-click for new tab.
            navigate('/backoffice/dashboard');
          }
        },
      ]
    });
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 pl-4 border-l border-gray-200 hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors"
      >
        <div className="text-right hidden md:block">
          <p className="text-sm font-medium text-gray-900">{user?.name || "User"}</p>
          <p className="text-xs text-gray-500">
            {company?.customer_id && <span className="font-mono text-violet-600 mr-1">{company.customer_id}</span>}
            {company?.name || company?.company_name || "No company"}
          </p>
        </div>
        <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm">
          <span className="text-sm font-semibold text-white">
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* User Info Header */}
          <div className="px-4 py-4 bg-gradient-to-r from-violet-50 to-indigo-50 border-b">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-lg font-semibold text-white">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{user?.name || "User"}</p>
                <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {user?.email}
                </p>
                {company?.customer_id && (
                  <p className="text-xs text-violet-600 font-mono mt-0.5">
                    ID: {company.customer_id}
                  </p>
                )}
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="flex gap-4 mt-3 pt-3 border-t border-violet-100">
              <div className="text-center flex-1">
                <p className="text-lg font-bold text-gray-900">{subscription?.plan_id?.replace("_", " ") || "Free"}</p>
                <p className="text-[10px] text-gray-500 uppercase">Plan</p>
              </div>
              <div className="text-center flex-1">
                <p className="text-lg font-bold text-gray-900">
                  {user?.two_factor_enabled ? "✓" : "✗"}
                </p>
                <p className="text-[10px] text-gray-500 uppercase">2FA</p>
              </div>
              {hasAdminAccess && (
                <div className="text-center flex-1">
                  <p className="text-lg font-bold text-violet-600">
                    <Shield className="w-5 h-5 mx-auto" />
                  </p>
                  <p className="text-[10px] text-gray-500 uppercase">Admin</p>
                </div>
              )}
            </div>
          </div>

          {/* Menu Sections */}
          <div className="max-h-80 overflow-y-auto">
            {menuItems.map((section, sIndex) => (
              <div key={section.section} className={sIndex > 0 ? "border-t border-gray-100" : ""}>
                <p className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50">
                  {section.section}
                </p>
                {section.items.map((item) => (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{item.label}</p>
                      {item.description && (
                        <p className="text-xs text-gray-500 truncate">{item.description}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 p-2">
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-red-50 rounded-lg transition-colors text-left group"
            >
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0 group-hover:bg-red-200">
                <LogOut className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-600">Sign Out</p>
                <p className="text-xs text-gray-500">End your session</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
